// Shape of webshared/controllers/eventsList.cfc's getEventsListData response, after
// camelizeResponse() (see keyMap.ts). Booleans coming from a DB tinyint column or CFML boolean
// arrive loosely typed (0/1/true/false) - coerced with truthy() where used, same convention as
// web/pages/event-page/src/data/types.ts.

export interface Viewer {
  loggedIn: unknown
  type: string
  trackId: number | string
  firstName: string
  lastName: string
  // racer-only: racerteam.id if this racer manages a team, else 0 (see session.teamManager)
  teamManager: unknown
  // track-only: empty string means not yet upgraded to a paid Stripe merchant account
  stripeMerchantId: string
  // Always a ready-to-use /shared/common/displayImage.cfm URL (stock-image fallback included).
  profilePicUrl: string
}

/** One event row, shared by the month-grouped list/flyers views. All date fields are ISO 8601
 *  strings (or "" when not set - see eventsList.cfc's isoOrEmpty()), same convention as
 *  event-page's EventPageEvent. */
export interface EventSummary {
  id: number
  name: string
  trackName: string
  city: string
  state: string
  startDT: string
  endDT: string
  flyerUrl: string
  /** 'offroad' | 'onroad' | 'oval' | '' - best-effort bucket derived server-side from
   *  classprofile.powerType substrings (see eventsList.cfc). '' means no class powerType matched
   *  any bucket - the event still renders, just isn't selectable by the Racing type filter. */
  raceType: string
  seriesName: string
  signupWindowOpen: unknown
  full: unknown
  openPaymentDT: string
  closePaymentDT: string
  entryLimit: number | string
  entryCount: number | string
  racerLimit: number | string
  racerCount: number | string
  classCount: number
  /** >0 when the logged-in racer has at least one entry in this event (drives the "You're
   *  entered" status override and the CTA). Always 0 for anonymous/track viewers. */
  enteredClassCount: number
  enteredPaid: unknown
  /** Distance in miles from the viewer's chosen location to this event's track, or null when no
   *  location is set OR the track has no latitude/longitude on file. */
  distanceMi: number | null
}

export interface MonthGroup {
  name: string
  count: number
  events: EventSummary[]
}

/** The racer's own soonest upcoming entered event, pinned above Featured (decision: single
 *  soonest only, not a list). Null when the viewer isn't a logged-in racer or has no upcoming
 *  entries. */
export interface YourEvent {
  id: number
  name: string
  trackName: string
  city: string
  state: string
  startDT: string
  endDT: string
  flyerUrl: string
  enteredClassCount: number
  enteredPaid: unknown
}

/** Sitewide paid-promotion card - same events.cfc::getFeaturedEvents()/listEvents(racetime=1)
 *  data event-page's sidebar already renders (SponsorsAndExtras.tsx), reused here as-is. */
export interface PromoEvent {
  id: number
  name: string
  trackName: string
  city: string
  state: string
  startDT: string
  endDT: string
  flyerUrl: string
  signupWindowOpen: unknown
  full: unknown
  openPaymentDT: string
  closePaymentDT: string
  entryLimit: number | string
  entryCount: number | string
  racerLimit: number | string
  racerCount: number | string
  classCount: number
  priceFrom: number | string
  enteredClassCount: number
  enteredPaid: unknown
}

export interface FilterOption {
  value: string
  label: string
  count: number
}

export interface SeriesOption {
  trackId: number
  name: string
  count: number
}

export interface FilterOptions {
  registration: FilterOption[]
  raceType: FilterOption[]
  /** Real vehicletypecodes.typeName values (e.g. "1/10th Electric Truck", "1/8th Scale Brushless
   *  Truck") - an open-ended, data-driven list (unlike raceType's fixed 3 buckets), sorted by
   *  count descending server-side. */
  vehicleType: FilterOption[]
  series: SeriesOption[]
}

export interface EventsListData {
  success: unknown
  error?: string
  viewer: Viewer
  yourEvent: YourEvent | null
  featured: PromoEvent[]
  raceTime: PromoEvent[]
  months: MonthGroup[]
  filters: FilterOptions
  totalCount: number
}

/** DB tinyint / CF struct values (0, "0", 1, "1", true, false, "Yes") coerced to a real boolean. */
export function truthy(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') return value !== '' && value !== '0' && value.toLowerCase() !== 'no' && value.toLowerCase() !== 'false'
  return false
}
